import React, { useEffect, useRef, useState, useCallback } from "react";
import "../Style/videoservices.css";

import video1 from "../assets/icon1.png";
import video2 from "../assets/icon2.png";
import video3 from "../assets/icon3.png";
import video4 from "../assets/icon4.png";
import video5 from "../assets/icon5.png";
import video6 from "../assets/icon6.png";

const services = [
  {
    icon: video1,
    title: "Corporate Video Production",
    desc: "Corporate videos showcase your company's vision, values, and services. We create professional videos that build trust and strengthen your brand image.",
    tags: [
      "Corporate Videos",
      "Company Profile",
      "Business Videos",
      "Brand Story",
      "Professional Shoots",
    ],
  },
  {
    icon: video2,
    title: "Promotional & Marketing Videos",
    desc: "Promotional videos are designed to attract customers and increase conversions. We craft engaging content that highlights your products and services.",
    tags: [
      "Promo Videos",
      "Product Videos",
      "Campaign Videos",
      "Ad Films",
      "Marketing Content",
    ],
  },
  {
    icon: video3,
    title: "Social Media Video Content",
    desc: "Short-form videos are essential for digital growth. We create eye-catching content optimized for platforms like Instagram, YouTube, and Facebook.",
    tags: [
      "Reels",
      "Shorts",
      "Social Media Ads",
      "Viral Content",
      "Digital Campaigns",
    ],
  },
  {
    icon: video4,
    title: "Explainer Videos",
    desc: "Explainer videos simplify complex ideas into clear and engaging visuals. Perfect for startups and SaaS products to communicate value effectively.",
    tags: [
      "Animated Videos",
      "Product Explainers",
      "Demo Videos",
      "Motion Graphics",
      "Storytelling",
    ],
  },
  {
    icon: video5,
    title: "Event & Interview Videos",
    desc: "We capture important moments through professional event coverage and interview videos that reflect authenticity and emotion.",
    tags: [
      "Event Coverage",
      "Interviews",
      "Testimonials",
      "Live Events",
      "Highlights",
    ],
  },
  {
    icon: video6,
    title: "Creative Video Solutions",
    desc: "Every brand is unique. We provide customized video solutions with creative direction, scripting, and editing to match your business goals.",
    tags: [
      "Custom Videos",
      "Creative Direction",
      "Video Editing",
      "Visual Effects",
      "Unique Concepts",
    ],
  },
];

const AUTOPLAY_DELAY = 4500;

function ServicesSection() {
  const sectionRef = useRef(null);
  const headingRef = useRef(null);
  const subtextRef = useRef(null);
  const autoplayRef = useRef(null);

  const [sectionVisible, setSectionVisible] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const total = services.length;

  // Scroll-reveal for heading/subtext
  useEffect(() => {
    const targets = [headingRef.current, subtextRef.current].filter(Boolean);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setSectionVisible(true);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.15 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  // Autoplay
  useEffect(() => {
    if (isPaused) return;
    autoplayRef.current = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % total);
    }, AUTOPLAY_DELAY);
    return () => clearInterval(autoplayRef.current);
  }, [isPaused, total]);

  const goTo = useCallback(
    (index) => {
      setActiveIndex(((index % total) + total) % total);
    },
    [total]
  );

  const goPrev = () => goTo(activeIndex - 1);
  const goNext = () => goTo(activeIndex + 1);

  // Position of each card relative to the active one (-1, 0, 1, or "far")
  const getPosition = (index) => {
    let diff = index - activeIndex;
    if (diff > total / 2) diff -= total;
    if (diff < -total / 2) diff += total;

    if (diff === 0) return "active";
    if (diff === -1) return "prev";
    if (diff === 1) return "next";
    return "far";
  };

  return (
    <section
      className="video-services-section"
      ref={sectionRef}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <span className="video-services-watermark" aria-hidden="true">
        SERVICES
      </span>

      <span
        className={`video-services-eyebrow ${
          sectionVisible ? "video-reveal" : ""
        }`}
      >
        What We Offer
      </span>

      <h2
        className={`video-services-heading ${
          sectionVisible ? "video-reveal" : ""
        }`}
        ref={headingRef}
      >
        Need for Video Production Services for your business
      </h2>

      <p
        className={`video-services-subtext ${
          sectionVisible ? "video-reveal" : ""
        }`}
        ref={subtextRef}
      >
        We create high-quality video content that captures attention and
        tells your brand story effectively. From promotional videos to
        corporate storytelling, our productions help engage your audience
        and boost brand visibility.
      </p>

      <div className="video-carousel">
        <button
          type="button"
          className="video-carousel-arrow video-carousel-arrow-left"
          onClick={goPrev}
          aria-label="Previous service"
        >
          ‹
        </button>

        <div className="video-carousel-track">
          {services.map((service, index) => {
            const position = getPosition(index);
            return (
              <div
                className={`video-service-card is-${position}`}
                key={service.title}
                onClick={() => position !== "active" && goTo(index)}
              >
                <div className="video-service-icon-wrap">
                  <img
                    src={service.icon}
                    alt={service.title}
                    className="video-service-icon"
                  />
                </div>

                <h3>{service.title}</h3>
                <p>{service.desc}</p>

                <div className="video-service-tags">
                  {service.tags.slice(0, 3).map((tag, i) => (
                    <span key={i}>{tag}</span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <button
          type="button"
          className="video-carousel-arrow video-carousel-arrow-right"
          onClick={goNext}
          aria-label="Next service"
        >
          ›
        </button>
      </div>

      <div className="video-carousel-dots">
        {services.map((_, index) => (
          <button
            key={index}
            type="button"
            className={`video-dot ${index === activeIndex ? "is-active" : ""}`}
            onClick={() => goTo(index)}
            aria-label={`Go to service ${index + 1}`}
          />
        ))}
      </div>
    </section>
  );
}

export default ServicesSection;